import { useState, useEffect } from 'react';
import { getAuditLogs, type AuditLog } from '../../api/audit.api';

export default function Auditoria() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'SUPERADMIN';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await getAuditLogs();
      setLogs(data);
    } catch (error) {
      console.error('Error cargando logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.accion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.entidad.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (log.usuario.company?.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getActionColor = (action: string) => {
    if (action.includes('CREATE')) return 'text-green-600 bg-green-50';
    if (action.includes('UPDATE')) return 'text-blue-600 bg-blue-50';
    if (action.includes('DELETE')) return 'text-red-600 bg-red-50';
    return 'text-slate-600 bg-slate-50';
  };

  return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Auditoría del Sistema</h1>
            <p className="text-slate-500 mt-1">Historial de acciones y cambios en la plataforma</p>
          </div>
          <button onClick={loadData} className="p-2 hover:bg-slate-100 rounded-lg transition">
            <i className="ri-refresh-line text-xl text-slate-500"></i>
          </button>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Buscar por usuario, acción, entidad o empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Vista de tabla - Desktop */}
        <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Usuario</th>
                {isSuperAdmin && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Empresa</th>
                )}
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Acción</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Entidad</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                   <td colSpan={isSuperAdmin ? 6 : 5} className="px-6 py-12 text-center text-slate-500">Cargando...</td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                   <td colSpan={isSuperAdmin ? 6 : 5} className="px-6 py-12 text-center text-slate-500">No hay registros de auditoría</td>
                </tr>
              ) : (
                filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{log.usuario.nombre}</p>
                        <p className="text-xs text-slate-500">{log.usuario.email}</p>
                      </div>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-600">
                          {log.usuario.company?.nombre || 'N/A'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.accion)}`}>
                        {log.accion}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {log.entidad} <span className="text-xs text-slate-400">#{log.entidadId?.slice(-6)}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                      {log.descripcion || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Vista de tarjetas - Móvil */}
        <div className="md:hidden space-y-3">
          {isLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-slate-500"></i>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
              No hay registros de auditoría
            </div>
          ) : (
            filteredLogs.map(log => (
              <div key={log.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 mb-1">{log.usuario.nombre}</div>
                    <div className="text-xs text-slate-500 truncate">{log.usuario.email}</div>
                    {isSuperAdmin && log.usuario.company?.nombre && (
                      <div className="text-xs text-slate-500 mt-1">
                        <i className="ri-building-line mr-1"></i>
                        {log.usuario.company.nombre}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ml-2 ${getActionColor(log.accion)}`}>
                    {log.accion}
                  </span>
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-folder-line text-slate-400"></i>
                    <span className="font-medium text-slate-700">{log.entidad}</span>
                    <span className="text-xs text-slate-400">#{log.entidadId?.slice(-6)}</span>
                  </div>
                  {log.descripcion && (
                    <div className="text-sm text-slate-600 pl-6">
                      {log.descripcion}
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center text-xs text-slate-500">
                  <i className="ri-time-line mr-1"></i>
                  {new Date(log.createdAt).toLocaleString('es-CO', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
  );
}
